import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { uploadDirect } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Loading } from '../../components/Ui';
import { onRealtime, joinTicket, leaveTicket } from '../../services/realtime';
import { useToast } from '../../context/ToastContext';
import { TICKET_STATUS, when } from './SupportPage';
import { colors } from '../../theme';

/**
 * One support conversation, live.
 *
 * Messages arrive over the socket while the page is open, so a reply from
 * support lands without a refresh.
 */
const TicketChatPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('open');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.support.ticket(id);
      setTicket(res.data);
      setMessages(res.data.messages || []);
      setStatus(res.data.status);
    } catch (e) {
      toast.error(e.message || 'Could not open that ticket');
      navigate('/support');
    } finally { setLoading(false); }
  }, [id, navigate, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    joinTicket(id);
    const offMsg = onRealtime('ticket:message', (p) => {
      if (String(p.ticketId) !== String(id)) return;
      setMessages((list) => (list.some((m) => String(m._id) === String(p.message && p.message._id))
        ? list : [...list, p.message]));
    });
    const offStatus = onRealtime('ticket:status', (p) => {
      if (String(p.ticketId) === String(id)) setStatus(p.status);
    });
    return () => { leaveTicket(id); offMsg(); offStatus(); };
  }, [id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async (attachments = []) => {
    const body = text.trim();
    if (!body && !attachments.length) return;
    setSending(true);
    try {
      const res = await api.support.reply(id, { text: body, attachments });
      setText('');
      setMessages((list) => (list.some((m) => String(m._id) === String(res.data._id))
        ? list : [...list, res.data]));
      if (status === 'resolved') setStatus('open');
    } catch (e) {
      toast.error(e.message || 'Not sent');
    } finally { setSending(false); }
  };

  const attach = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setProgress(0);
    try {
      const url = await uploadDirect(file, 'support', setProgress);
      await send([{ url, name: file.name, mime: file.type || '', size: file.size || 0 }]);
    } catch (err) {
      toast.error(err.message || 'Could not upload that file');
    } finally { setProgress(null); e.target.value = ''; }
  };

  if (loading) return <Loading text="Opening ticket…" />;

  const s = TICKET_STATUS[status] || TICKET_STATUS.open;

  return (
    <div style={{ maxWidth: 720 }}>
      <PageHeader
        title={ticket?.subject || 'Ticket'}
        subtitle={`${ticket?.ticketNo}${ticket?.refNo ? ` · ${ticket.refNo}` : ''}`}
      />

      <div className="flex" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ width: 8, height: 8, borderRadius: 4, background: s.tone, display: 'inline-block' }} />
        <span className="t-small" style={{ color: s.tone, fontWeight: 800 }}>{s.label}</span>
        {status === 'in_query' ? <span className="t-tiny">Support is waiting for your reply</span> : null}
      </div>

      <div className="card sh-sm" style={{ display: 'grid', gap: 10, maxHeight: '56vh', overflowY: 'auto' }}>
        {messages.map((m) => {
          const mine = m.from === 'user';
          return (
            <div
              key={m._id}
              style={{
                justifySelf: mine ? 'end' : 'start',
                maxWidth: '80%',
                background: mine ? colors.primary : 'var(--bg)',
                color: mine ? '#fff' : 'inherit',
                borderRadius: 12, padding: '10px 12px',
              }}
            >
              {!mine ? (
                <div className="t-tiny" style={{ color: colors.primary, fontWeight: 800 }}>
                  {m.authorName || 'KAAMWALA Support'}
                </div>
              ) : null}
              {m.text ? <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{m.text}</div> : null}
              {(m.attachments || []).map((f) => (
                <a
                  key={f.url}
                  href={f.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'block', fontSize: 12, marginTop: 6,
                    color: mine ? '#fff' : colors.primary, textDecoration: 'underline',
                  }}
                >
                  📎 {f.name || 'Attachment'}
                </a>
              ))}
              <div style={{ fontSize: 10.5, opacity: 0.7, marginTop: 4 }}>{when(m.at)}</div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="flex" style={{ gap: 8, marginTop: 12 }}>
        <label className="btn ghost" style={{ cursor: 'pointer' }}>
          {progress !== null ? `${Math.round(progress * 100)}%` : '📎'}
          <input type="file" hidden onChange={attach} />
        </label>
        <input
          className="input grow"
          placeholder="Write a message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <button type="button" className="btn primary" disabled={sending || !text.trim()} onClick={() => send()}>
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default TicketChatPage;
